import { checkFail, type PresentCheck } from './core/core.ts';
import type { IfEqual, Present, RecordKey } from './identity.ts';
import { isArray, isIterable } from './identity.ts';

//
// Strings (as arrays of characters)
//

// Divides `str` into two parts:
//   1) Before the first occurrance of `delimiter`.
//   2) After the end of the first occurance of `delimiter`.
// N.B. Other languages use str.split(delimiter, 2) for this, but JavaScript's `split` doesn't work
// like that.
export function splitOn(str: string, delimiter: string): [string, string | undefined] {
  const i = str.indexOf(delimiter);
  return i < 0 ? [str, undefined] : [str.slice(0, i), str.slice(i + delimiter.length)];
}

//
// Maps, Records, and Objects
//

export function mapFrom<K extends RecordKey, V>(values: V[], keyOf: (v: V) => K) {
  const result = new Map<K, V>();
  for (const v of values) {
    result.set(keyOf(v), v);
  }
  return result;
}

// Fetches `key` from `map`, if present. Otherwise, `create` a new value, store it in the map,
// and return that.
export function getWithDefault<K, V>(map: Map<K, Present<V>>, key: K, create: () => Present<V>) {
  let value = map.get(key);
  if (value === undefined) {
    value = create();
    map.set(key, value);
  }
  return value;
}

//
// Arrays
//

// DNS: How to prevent T from being an array type?
// This version doesn't work when T is a string literal union. See KeyConfig.
export type Some<T> = T extends unknown[] ? never : T | readonly T[];

export function asArray2(
  v: unknown,
  params?: { consumeIterable?: boolean; check: typeof PresentCheck },
): Array<unknown> | ReadonlyArray<unknown>;
export function asArray2(
  v: unknown,
  params?: { consumeIterable?: boolean; check?: typeof PresentCheck },
): Array<unknown> | ReadonlyArray<unknown> | undefined;
export function asArray2(
  v: unknown,
  params?: { consumeIterable?: boolean; check?: typeof PresentCheck },
): Array<unknown> | ReadonlyArray<unknown> | undefined {
  if (isArray(v)) {
    return v;
  }
  const consumeIterable = params?.consumeIterable ?? false;
  if (consumeIterable) {
    if (isIterable(v)) {
      return Array.from(v);
    }
  }
  if (params?.check) {
    checkFail(
      'asArray2 failed.',
      () => `Input type ${typeof v} is not an array${consumeIterable ? ' or iterable' : ''}.`,
    );
  }
  return undefined;
}

export function asArray<T>(value: T | readonly T[]): readonly T[];
export function asArray<T>(value: T | readonly T[] | undefined): readonly T[] | undefined;
export function asArray<T>(
  value: T | readonly T[] | undefined,
  defaultValue: readonly T[],
): readonly T[];
export function asArray<T>(
  value: T | readonly T[] | undefined,
  defaultValue?: readonly T[],
): readonly T[] | undefined {
  return isArray(value)
    ? (value as readonly T[])
    : value !== undefined
      ? ([value] as readonly T[])
      : defaultValue;
}

export function includes<T extends V, V>(array: readonly T[], value: V): value is T {
  return array.includes(value as T);
}

export function firstOf<T>(array: readonly T[]): T | undefined {
  return array.length ? array[0] : undefined;
}

export function lastOf<T>(array: readonly T[]): T | undefined {
  return array.length ? array[array.length - 1] : undefined;
}

// This is just <Array>.indexOf, except that it accepts a |needle| that has _any_ type intersection
// with |haystack|.
export function indexOf<N, H>(
  needle: N,
  haystack: readonly H[],
): IfEqual<N & H, never, never, number> {
  return haystack.indexOf(needle as unknown as H) as IfEqual<N & H, never, never, number>;
}

// export function find<T>(predicate: (value: T, index: number, obj: readonly T[]) => value is T, haystack: readonly T[]): T | undefined {
//   return haystack.find(predicate);
// }

// If `array` contains `value`, then the first occurrance is removed and `true` is returned.
// N.B. The removal changes the order of `array`, but is O(1).
export function remove<T>(array: Array<T>, value: T): boolean {
  const index = array.indexOf(value);
  if (index < 0) {
    return false;
  }
  const last = array.pop()!;
  if (index < array.length) {
    array[index] = last;
  }
  return true;
}

// If `array` contains a value that satisfies `pred`, then the first occurrance is removed and
// the value is returned. Otherwise, undefined is returned.
// N.B. The removal changes the order of `array`, but is O(1).
export function removeIf<T>(array: Array<T>, pred: (v: T) => boolean): T | undefined {
  const index = array.findIndex(pred);
  if (index < 0) {
    return undefined;
  }
  const result = array[index];
  const last = array.pop()!;
  if (index < array.length) {
    array[index] = last;
  }
  return result;
}

// In-place filter that is equivalent to `array = array.filter(pred)`.
export function filter<T>(array: Array<T>, pred: (v: T) => boolean) {
  let length = 0;
  for (const el of array) {
    if (pred(el)) {
      array[length++] = el;
    }
  }
  array.length = length;
}

export function mapAndFilter<T, U>(
  array: ReadonlyArray<T>,
  transform: (value: T, index: number, array: readonly T[]) => U | undefined,
): U[] {
  return array.map(transform).filter((u) => u !== undefined);
}

// DNS: Replace this with Object.groupBy.
// Split an array into two or more arrays based on whether each elements matches predicate p.
// Matches of the first predicate return in the first result, second in the second, and any
// unmatched values in the last result.
export function partition<T, K extends 1 | 2 | 3 | 4 | 5>(
  values: Iterable<T>,
  ...preds: TupleOf<(value: T) => boolean, K>
): [T[], ...TupleOf<T[], K>] {
  // https://medium.com/@wisecobbler/4-ways-to-populate-an-array-in-javascript-836952aea79f
  const results: T[][] = Array.from({ length: preds.length + 1 }, () => []);
  for (const v of values) {
    let index = preds.findIndex((p) => p(v));
    if (index < 0) {
      index = preds.length;
    }
    results[index].push(v);
  }
  return results as [T[], ...TupleOf<T[], K>];
}

export function minMax<T>(values: T[], isLess: (a: T, b: T) => boolean): [T, T] | undefined {
  if (!values.length) {
    return undefined;
  }
  let min = values[0];
  let max = values[0];
  for (let i = 1; i < values.length; ++i) {
    const v = values[i];
    if (isLess(v, min)) {
      min = v;
    } else if (isLess(max, v)) {
      max = v;
    }
  }
  return [min, max];
}

// TODO: bubbleUnshift?
// Pushes `value` onto `arr`, and then bubbles it forward until `inOrder` is satisfied.
// The original array is returned, but keep in mind that this operation is done in place.
export function bubblePush<T>(arr: T[], value: T, inOrder: (a: T, b: T) => boolean) {
  arr.push(value);
  for (let i = arr.length - 1; i > 0; --i) {
    if (inOrder(arr[i - 1], value)) {
      arr[i] = value;
      return arr;
    }
    arr[i] = arr[i - 1];
  }
  arr[0] = value;
  return arr;
}

export function indicies(length: number, start = 0) {
  // https://stackoverflow.com/a/33352604/8107589
  return Array.from({ length }, (_, i) => i + start);
}

export function arrayOf<V>(length: number, value: V): Array<V>;
export function arrayOf<V>(length: number, value: V, readonly: 'readonly'): ReadonlyArray<V>;
export function arrayOf<V>(length: number, value: V, readonly?: 'readonly') {
  const result = Array<V>(length).fill(value);
  return readonly === 'readonly' ? Object.freeze(result) : result;
}

//
// Tuples
//

// It's possible this belongs in identity.ts, instead.
// https://github.com/Microsoft/TypeScript/issues/26223#issuecomment-674500430
// export type TupleOf<T, N extends number> = N extends N
//   ? number extends N
//     ? readonly T[]
//     : TupleOfImpl<T, N, readonly []>
//   : never;
// type TupleOfImpl<T, N extends number, R extends readonly unknown[]> = R['length'] extends N
//   ? R
//   : TupleOfImpl<T, N, readonly [T, ...R]>;

// https://github.com/microsoft/TypeScript/pull/40002
export type TupleOf<T, N extends number> = N extends N
  ? number extends N
    ? never // T[]
    : TupleOf_<T, N, []>
  : never;
type TupleOf_<T, N extends number, R extends unknown[]> = R['length'] extends N
  ? R
  : TupleOf_<T, N, [T, ...R]>;

export type ArTupOf<T, N extends number> = number extends N ? T[] : TupleOf<T, N>;

// export type TupleOf<T, N extends number, A extends any[] = []> = A['length'] extends N ? A : TupleOf<T, N, [...A, T]>;

export type ReadonlyTupleOf<T, N extends number> = N extends N
  ? number extends N
    ? never // readonly T[]
    : ReadonlyTupleOf_<T, N, readonly []>
  : never;
type ReadonlyTupleOf_<T, N extends number, R extends readonly unknown[]> = R['length'] extends N
  ? R
  : ReadonlyTupleOf_<T, N, readonly [T, ...R]>;

// Doesn't seem to work. Not sure why this is here.
// export function mapTuple<T extends ReadonlyArray<unknown>, V extends ReadonlyArray<unknown>>(
//   tuple: T,
//   fn: (value: T[number], index: number, arr: ReadonlyArray<unknown>) => V,
// ): { [K in keyof T]: V[K] } {
//   return tuple.map(fn) as { [K in keyof T]: V };
// }

//
// Set operations
//

export function unique<T>(array: readonly T[], key?: (t: T) => unknown): T[] {
  if (key) {
    return Array.from(new Map(array.map((t) => [key(t), t])).values());
  } else {
    return Array.from(new Set(array).values());
  }
}

export function setEquals<T>(a: readonly T[], b: readonly T[]) {
  if (a.length !== b.length) {
    return false;
  }
  const sb = new Set(b);
  return a.every((el) => sb.has(el));
}

export function setUnion<T>(a: readonly T[], b: readonly T[]) {
  return unique([...a, ...b]);
}

// N.B. Arrays must be pre-sorted.
export function symmetricDifference<T>(a: readonly T[], b: readonly T[]): readonly T[] {
  const diff: T[] = [];
  let ai = 0;
  let bi = 0;
  while (ai < a.length && bi < b.length) {
    const ae = a[ai];
    const be = b[bi];
    if (ae === be) {
      ai += 1;
      bi += 1;
    } else if (ae < be) {
      diff.push(ae);
      ai += 1;
    } else {
      diff.push(be);
      bi += 1;
    }
  }
  diff.push(...a.slice(ai), ...b.slice(bi));
  return diff;
}
